<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("account.menu.items") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="md" type="neutral" @click="handleCreate()">
            <i class="fa fa-plus"></i> {{ $t("add") }}
          </base-button>
        </div>
      </div>
    </base-header>
    <div class="container-fluid mt--6">
      <div>
        <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
          <template slot="header">
            <div class="row">
              <div class="col-md-3">
                <base-input
                  v-model="searchQuery"
                  prepend-icon="fas fa-search"
                  :placeholder="$t('search')"
                >
                </base-input>
              </div>
              <div class="col-md-2">
                <base-input>
                  <el-select v-model="filterType" :placeholder="$t('account.type')" @change="handleSearch">
                    <el-option :label="$t('account.all')" :value="0"></el-option>
                    <el-option :label="$t('account.income')" :value="1"></el-option>
                    <el-option :label="$t('account.expense')" :value="2"></el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-md-2">
                <base-input>
                  <el-select v-model="filterActive" :placeholder="$t('status')" @change="handleSearch">
                    <el-option :label="$t('account.all')" value=""></el-option>
                    <el-option :label="$t('account.active')" value="true"></el-option>
                    <el-option :label="$t('account.inactive')" value="false"></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
          </template>
          <div>
            <el-table
              :data="queriedData"
              row-key="id"
              header-row-class-name="thead-light"
              v-loading="loading"
            >
              <el-table-column :label="$t('account.item_name')" min-width="260">
                <div slot-scope="{ row }" class="account-tree-cell" :style="{ paddingLeft: rowDepth(row) * 22 + 'px' }">
                  <i v-if="rowDepth(row) > 0" class="fa fa-level-up-alt fa-rotate-90 text-muted mr-2 account-tree-elbow"></i>
                  <span :class="{ 'font-weight-bold': rowDepth(row) === 0 }">{{ row.name }}</span>
                  <small v-if="rowDepth(row) > 0" class="d-block text-muted account-tree-path">{{ row.path_name }}</small>
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.type')" min-width="120" align="center">
                <div slot-scope="{ row }">
                  <badge :type="row.type === 1 ? 'success' : 'danger'" class="badge-lg">
                    {{ row.type === 1 ? $t('account.income') : $t('account.expense') }}
                  </badge>
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.platform')" min-width="150">
                <div slot-scope="{ row }">
                  <template v-if="row.type === 2 && row.effective_platform_name">
                    <span>{{ row.effective_platform_name }}</span>
                    <badge v-if="row.platform_inherited" type="secondary" class="ml-1" :title="row.platform_inherited_from">
                      {{ $t('account.platform_inherited') }}
                    </badge>
                  </template>
                  <span v-else class="text-muted">-</span>
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.description')" min-width="150">
                <div slot-scope="{ row }">
                  {{ row.description || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('status')" min-width="130" align="center">
                <div slot-scope="{ row }">
                  <badge :type="row.is_active ? 'success' : 'secondary'" class="badge-lg">
                    {{ row.is_active ? $t('account.active') : $t('account.inactive') }}
                  </badge>
                </div>
              </el-table-column>

              <el-table-column :label="$t('actions')" min-width="150" align="center">
                <div slot-scope="{ row }" class="d-flex justify-content-center">
                  <base-button
                    @click.native="handleCreateSub(row)"
                    class="add-sub"
                    type="info"
                    size="sm"
                    icon
                    :title="$t('account.add_sub')"
                  >
                    <i class="text-white fa fa-plus"></i>
                  </base-button>
                  <base-button
                    @click.native="handleEdit(row)"
                    class="edit"
                    type="warning"
                    size="sm"
                    icon
                  >
                    <i class="text-white fa fa-edit"></i>
                  </base-button>
                  <base-button
                    @click.native="handleDelete(row)"
                    class="remove btn-link"
                    type="danger"
                    size="sm"
                    icon
                  >
                    <i class="text-white fa fa-trash"></i>
                  </base-button>
                </div>
              </el-table-column>
            </el-table>
          </div>
          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            v-if="queriedData.length != 0"
          >
            <div class="">
              <p class="card-category">
                {{ $t("data") }} {{ from + 1 }} {{ $t("to") }} {{ to }}
                {{ $t("of") }} {{ total }}
              </p>
            </div>
            <base-pagination
              class="pagination-no-border"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="total"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <modal :show.sync="showModal" modal-classes="modal-secondary">
      <h5 slot="header" class="modal-title">
        {{ isEdit ? $t('account.edit_item') : $t('account.create_item') }}
      </h5>
      <form class="new-event--form" @submit.prevent="">
        <base-input :label="$t('account.item_name')" v-model="formData.name" required>
        </base-input>

        <div class="form-group">
          <label class="form-control-label">{{ $t('account.type') }}</label>
          <el-select v-model="formData.type" class="w-100" :disabled="isEdit || !!formData.parent_id" @change="handleTypeChange">
            <el-option :label="$t('account.income')" :value="1"></el-option>
            <el-option :label="$t('account.expense')" :value="2"></el-option>
          </el-select>
        </div>

        <div class="form-group">
          <label class="form-control-label">{{ $t('account.parent') }}</label>
          <el-select
            v-model="formData.parent_id"
            class="w-100 account-parent-select"
            filterable
            clearable
            :placeholder="$t('account.parent_none')"
            popper-class="account-tree-popper"
          >
            <el-option
              v-for="opt in parentChoices"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            >
              <span :style="{ paddingLeft: opt.depth * 14 + 'px' }">{{ opt.label }}</span>
            </el-option>
          </el-select>
          <small class="text-muted">{{ $t('account.parent_hint') }}</small>
        </div>

        <div class="form-group" v-if="formData.type === 2">
          <label class="form-control-label">{{ $t('account.platform') }}</label>
          <el-select
            v-model="formData.platform_id"
            class="w-100 account-platform-select"
            clearable
            :placeholder="$t('account.platform_none')"
          >
            <el-option v-for="p in platforms" :key="p.id" :label="p.name" :value="p.id"></el-option>
          </el-select>
          <small v-if="!formData.platform_id && inherited" class="d-block text-info account-platform-inherited">
            {{ $t('account.platform_inherited_from', { path: inherited.fromPath, platform: inheritedName }) }}
          </small>
          <small class="d-block text-muted">{{ $t('account.platform_hint') }}</small>
        </div>

        <base-input :label="$t('account.description')" v-model="formData.description">
        </base-input>

        <div class="form-group">
          <label class="form-control-label">{{ $t('status') }}</label>
          <div>
            <el-switch
              v-model="formData.is_active"
              active-color="#13ce66"
              inactive-color="#909399"
            >
            </el-switch>
            <span class="ml-2">{{ formData.is_active ? $t('account.active') : $t('account.inactive') }}</span>
          </div>
        </div>
      </form>

      <template slot="footer">
        <button type="submit" class="btn btn-md btn-primary" @click="handleSave()">
          {{ $t("save") }}
        </button>
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showModal = false">
          {{ $t("close") }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Switch } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination, Badge } from "@/components";
import clientPaginationMixin from "../PaginatedTables/clientPaginationMixin";
import swal from "sweetalert2";
import { mapActions, mapGetters } from "vuex";
import AccountProvider from "@/resource/account";
import { apiErrorMessage } from "@/util/api-error";
import {
  treeRows,
  parentOptions,
  inheritedPlatform,
  platformName,
  itemPayload,
} from "@/utils/accountTree";

const accountApi = new AccountProvider();

export default {
  mixins: [clientPaginationMixin],
  components: {
    BasePagination,
    RouteBreadCrumb,
    Badge,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
  },
  computed: {
    ...mapGetters({
      items: "Account/getItems",
      platforms: "Account/getPlatforms",
    }),
    // The API returns categories already in tree order with path_name/depth;
    // an older API without those fields falls back to the client-side walk.
    tableData() {
      const rows = this.items || [];
      if (rows.length && rows[0].path_name === undefined) return treeRows(rows);
      return rows;
    },
    // Parents come from EVERY category (allItems), not the filtered table.
    parentChoices() {
      return parentOptions(this.allItems, {
        type: this.formData.type,
        selfId: this.isEdit ? this.formData.id : 0,
      });
    },
    inherited() {
      return inheritedPlatform(this.allItems, this.formData.parent_id);
    },
    inheritedName() {
      return this.inherited ? platformName(this.platforms, this.inherited.platformId) : "";
    },
  },
  data() {
    return {
      propsToSearch: ["name", "path_name", "description"],
      // a tree reads badly when split across pages of 15
      pagination: { perPage: 100, currentPage: 1, perPageOptions: [50, 100], total: 0 },
      allItems: [],
      loading: false,
      showModal: false,
      isEdit: false,
      filterType: 0,
      filterActive: "",
      formData: {
        id: 0,
        name: "",
        type: 1,
        description: "",
        is_active: true,
        parent_id: null,
        platform_id: null,
      },
    };
  },
  mounted() {
    this.loadData();
    // no platforms list = the platform select is simply empty; the page still works
    this.listPlatforms().catch(() => {});
  },
  methods: {
    ...mapActions({
      listItems: "Account/listItems",
      createItem: "Account/createItem",
      updateItem: "Account/updateItem",
      deleteItem: "Account/deleteItem",
      listPlatforms: "Account/listPlatforms",
    }),
    rowDepth(row) {
      if (row.depth !== undefined && row.depth !== null) return row.depth;
      return row._depth || 0;
    },
    errText(err) {
      return apiErrorMessage(err, (k, a) => this.$t(k, a));
    },
    // Every category, unfiltered — the parent picker and the inheritance hint
    // must see parents the table's filters hide.
    async loadAllItems() {
      try {
        const res = await accountApi.ItemList({});
        this.allItems = (res && res.data && res.data.Data) || [];
      } catch (e) {
        this.allItems = this.items || [];
      }
    },
    handleTypeChange() {
      if (this.formData.type !== 2) this.formData.platform_id = null;
      const parent = this.allItems.find((i) => i.id === this.formData.parent_id);
      if (parent && parent.type !== this.formData.type) this.formData.parent_id = null;
    },
    handleCreateSub(row) {
      this.isEdit = false;
      this.formData = {
        id: 0,
        name: "",
        type: row.type,
        description: "",
        is_active: true,
        parent_id: row.id,
        platform_id: null,
      };
      this.loadAllItems();
      this.showModal = true;
    },
    async loadData() {
      this.loading = true;
      try {
        const params = {};
        if (this.filterType > 0) params.type = this.filterType;
        if (this.filterActive !== "") params.is_active = this.filterActive;
        await this.listItems(params);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.loadData();
    },
    handleCreate() {
      this.isEdit = false;
      this.formData = {
        id: 0,
        name: "",
        type: 1,
        description: "",
        is_active: true,
        parent_id: null,
        platform_id: null,
      };
      this.loadAllItems();
      this.showModal = true;
    },
    handleEdit(row) {
      this.isEdit = true;
      this.formData = {
        id: row.id,
        name: row.name,
        type: row.type,
        description: row.description || "",
        is_active: row.is_active,
        parent_id: row.parent_id || null,
        platform_id: row.platform_id || null,
      };
      this.loadAllItems();
      this.showModal = true;
    },
    handleSave() {
      var self = this;
      if (!this.formData.name) {
        swal.fire({
          title: this.$t("account.error_name_required"),
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
        return;
      }

      const payload = itemPayload(this.formData);

      if (this.isEdit) {
        this.updateItem({ id: this.formData.id, payload })
          .then(function () {
            self.showModal = false;
            self.loadData();
            swal.fire({
              title: self.$t("edit_success"),
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch(function (err) {
            var errorMsg = self.errText(err);
            swal.fire({
              title: errorMsg,
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      } else {
        this.createItem(payload)
          .then(function () {
            self.showModal = false;
            self.loadData();
            swal.fire({
              title: self.$t("create_success"),
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch(function (err) {
            var errorMsg = self.errText(err);
            swal.fire({
              title: errorMsg,
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      }
    },
    handleDelete(row) {
      var self = this;
      swal
        .fire({
          title: this.$t("delete_confirm"),
          text: this.$t("delete_confirm_question") + row.name,
          icon: "warning",
          showCancelButton: true,
          confirmButtonClass: "btn btn-success btn-fill",
          cancelButtonClass: "btn btn-danger btn-fill",
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          buttonsStyling: false,
        })
        .then(function (result) {
          if (result.value) {
            self.deleteItem(row.id)
              .then(function () {
                self.loadData();
                swal.fire({
                  title: self.$t("delete_success"),
                  icon: "success",
                  confirmButtonClass: "btn btn-success btn-fill",
                  buttonsStyling: false,
                });
              })
              .catch(function (err) {
                var errorMsg = self.errText(err);
                swal.fire({
                  title: errorMsg,
                  icon: "error",
                  confirmButtonClass: "btn btn-danger btn-fill",
                  buttonsStyling: false,
                });
              });
          }
        });
    },
  },
};
</script>

<style>
.no-border-card .card-footer {
  border-top: 0;
}
.badge-lg {
  font-size: 0.8rem;
  padding: 0.5em 0.8em;
}
.account-tree-cell {
  line-height: 1.3;
}
.account-tree-path {
  font-size: 0.72rem;
  white-space: normal;
}
</style>
